import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "..";
import { setDrives } from "../features/shipBuilderSlice";
import { BreakawaySection } from "../../types/breakawayHull";
import { Drive } from "../../types/drive";

/**
 * Middleware to synchronize breakaway hull drives to the main ship drives array
 * This ensures that the main ship stats calculations include breakaway drive information
 */
const breakawayDriveSyncMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: unknown) => {
    // First, let the action pass through and update the state
    const result = next(action);

    // Check if this is a breakaway hull action that affects drives
    if (typeof action === 'object' && action !== null && 'type' in action && typeof action.type === 'string' && action.type.startsWith('breakawayHull/')) {
      const state = store.getState();
      const breakawayHull = state.breakawayHull;

      // If we're in breakaway mode and have sections with drives, sync them to the main drives array
      if (breakawayHull?.isBreakaway && breakawayHull.sections?.length > 0) {
        // Collect all drives from all sections
        const newDrives: Drive[] = [];
        
        breakawayHull.sections.forEach((section: BreakawaySection, sectionIndex: number) => {
          if (section.drives) {
            Object.entries(section.drives).forEach(([driveType, drive]: [string, Drive | null]) => {
              if (drive && drive.type && drive.rating !== undefined) {
                newDrives.push({
                  type: drive.type,
                  rating: drive.rating,
                  techLevel: drive.techLevel,
                  description: drive.description,
                  section: sectionIndex, // Add section info for reference
                });
              }
            });
          }
        });

        // Update the main drives array directly
        store.dispatch(setDrives(newDrives));

        // Note: We don't need to manually update ship stats here because:
        // 1. The main drives array is updated above with setDrives()
        // 2. The main ship stats calculation (calculateShipStats) already includes drive costs from state.drives
        // 3. The shipStatsMiddleware will automatically recalculate totals when drives change
      }
    }

    return result;
  };

export default breakawayDriveSyncMiddleware;
